import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { loadSavedFilters, saveFilters } from "@/lib/filterUtils";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download } from "lucide-react";
import IssueFilters from "./IssueFilters";
import PlacesSearchMap from "./GoogleApi";
import { CATEGORY_COLORS, CategoryType } from "@/pages/Dashboard";

interface Location {
  lat: number;
  lng: number;
  id: string;
  category?: string;
  status?: string;
  imageUrl?: string;
  severity?: number;
}

interface Filters {
  category: string;
  status: string;
  startDate?: Date;
  endDate?: Date;
  severityScore?: number;
  userType?: string;
  ward?: string;
}

interface GoogleMapProps {
  locations: Location[];
  height?: string;
  onFiltersChange?: (locations: Location[]) => void;
}

export default function GoogleMap({
  locations,
  height = "600px",
  onFiltersChange,
}: GoogleMapProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>(locations);
  const [filters, setFilters] = useState<Filters>(() => {
    // Load filters from localStorage on component mount
    const savedFilters = loadSavedFilters();
    return savedFilters || {
      category: "all",
      status: "all",
      userType: "all",
      ward: "all",
    };
  });
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setFilteredLocations(locations);
    setIsLoading(false);
  }, [locations]);

  const getMarkerColor = (status?: string) => {
    switch (status) {
      case "NEW":
        return "red";
      case "IN_REVIEW":
        return "orange";
      case "RESOLVED":
        return "green";
      case "REJECTED":
        return "gray";
      case "SPAM":
        return "purple";
      default:
        return "blue";
    }
  };

  const handleMarkerClick = (location: Location) => {
    navigate(`/issues/${location.id}`);
  };

  const handleFilterChange = async (newFilters: Filters) => {
    setFilters(newFilters);
    
    // Save filters to localStorage
    saveFilters(newFilters);
    
    setIsLoading(true);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found");

      const url = new URL(`${import.meta.env.VITE_BE_URL}/api/v1/reports`);
      url.searchParams.append("limit", "2000"); // Always get 2000 locations for comprehensive map view
      url.searchParams.append("severityScore", "70"); // Always filter by severity score 70

      // Add filters to URL
      if (newFilters.category !== "all") {
        url.searchParams.append("category", newFilters.category);
      }
      if (newFilters.status !== "all") {
        url.searchParams.append("status", newFilters.status);
      }
      if (newFilters.startDate) {
        url.searchParams.append("startDate", newFilters.startDate.toISOString().split('T')[0]);
      }
      if (newFilters.endDate) {
        url.searchParams.append("endDate", newFilters.endDate.toISOString().split('T')[0]);
      }

      if (newFilters.userType) {
        url.searchParams.append("userType", newFilters.userType);
      }
      if (newFilters.ward && newFilters.ward !== "all") {
        url.searchParams.append("ward", newFilters.ward);
      }

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch reports");
      }

      const data = await response.json();
      const locations = data.data.reports.map((report: any) => ({
        lat: report.lat,
        lng: report.lng,
        id: report.id,
        category: report.category,
        status: report.status,
        imageUrl: report.imageUrl,
      }));
      
      setFilteredLocations(locations);
      if (onFiltersChange) {
        onFiltersChange(locations);
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      console.error("Fetch map locations error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (exportFilters: Filters) => {
    try {
      setIsExporting(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found");

      const url = new URL(`${import.meta.env.VITE_BE_URL}/api/v1/reports/download-csv`);
      
      // Always use fixed values for map view
      url.searchParams.append("limit", "2000");
      url.searchParams.append("severityScore", "70");
      
      // Add user-editable filters to URL
      if (exportFilters.category !== "all") {
        url.searchParams.append("category", exportFilters.category);
      }
      if (exportFilters.status !== "all") {
        url.searchParams.append("status", exportFilters.status);
      }
      if (exportFilters.startDate) {
        url.searchParams.append("startDate", exportFilters.startDate.toISOString().split('T')[0]);
      }
      if (exportFilters.endDate) {
        url.searchParams.append("endDate", exportFilters.endDate.toISOString().split('T')[0]);
      }
      if (exportFilters.userType) {
        url.searchParams.append("userType", exportFilters.userType);
      }
      if (exportFilters.ward && exportFilters.ward !== "all") {
        url.searchParams.append("ward", exportFilters.ward);
      }

      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to export data");
      }

      const data = await response.json();
      
      if (data.success && data.data.downloadUrl) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = data.data.downloadUrl;
        link.download = data.data.filename || 'reports.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`CSV exported successfully! Total reports: ${data.data.totalReports}`);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to export data");
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <IssueFilters onFilterChange={handleFilterChange} initialFilters={filters} hideSeverityScore={true} />
        <Card className="p-6">
          <div className="space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="w-full" style={{ height }} />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Fixed Filter Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center text-blue-800">
          <div className="text-blue-600 mr-2">ℹ️</div>
          <div className="text-sm">
            <span className="font-medium">Map View Filters:</span> Showing all the issues with AI Severity Score more than 70. You can apply additional filters over the top of it.
          </div>
        </div>
      </div>
      
              <IssueFilters onFilterChange={handleFilterChange} initialFilters={filters} hideSeverityScore={true} />
      
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Issue Locations</h3>
          </div>

          {/* Category Legend */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Issue Categories</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {Object.entries(CATEGORY_COLORS).map(([category, color]) => (
                <div key={category} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full border-2 border-white shadow-sm" 
                    style={{ backgroundColor: color }}
                  ></div>
                  <span className="text-gray-600 capitalize">
                    {category.replace(/_/g, ' ').toLowerCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Map Component */}
          {filteredLocations.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                No locations to display on the map.
              </p>
            </div>
          ) : (
            <PlacesSearchMap
                    coordinates={{
                        lat: 28.4595,
                        lng: 77.0266,
                    }}
                    data={filteredLocations}
                    onMarkerClick={handleMarkerClick}
                    // setCoordinates={(coords) => handleMapClick(coords.lat, coords.lng)}
                />
           
          )}
        </div>
      </Card>
    </div>
  );
}
